import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindOperationChainItemsQueryResponse,
  FindOperationChainItemsPathParams,
} from "../types/FindOperationChainItems"

type FindOperationChainItemsClient = typeof client<
  FindOperationChainItemsQueryResponse,
  Error,
  never
>
type FindOperationChainItems = {
  data: FindOperationChainItemsQueryResponse
  error: Error
  request: never
  pathParams: FindOperationChainItemsPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindOperationChainItemsClient>>
  client: {
    parameters: Partial<Parameters<FindOperationChainItemsClient>[0]>
    return: Awaited<ReturnType<FindOperationChainItemsClient>>
  }
}
export const findOperationChainItemsQueryKey = (
  operationChainId: FindOperationChainItemsPathParams["operationChainId"]
) =>
  [
    {
      url: "/v1/operation-chain/find-operation-chain-items/:operationChainId",
      params: { operationChainId },
    },
  ] as const
export type FindOperationChainItemsQueryKey = ReturnType<
  typeof findOperationChainItemsQueryKey
>
export function findOperationChainItemsQueryOptions<
  TData = FindOperationChainItems["response"],
  TQueryData = FindOperationChainItems["response"],
>(
  operationChainId: FindOperationChainItemsPathParams["operationChainId"],
  options: FindOperationChainItems["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindOperationChainItems["response"],
    FindOperationChainItems["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findOperationChainItemsQueryKey(operationChainId)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindOperationChainItems["data"],
        FindOperationChainItems["error"]
      >({
        method: "get",
        url: `/v1/operation-chain/find-operation-chain-items/${operationChainId}`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find operation chain items
 * @link /v1/operation-chain/find-operation-chain-items/:operationChainId
 */
export function useFindOperationChainItems<
  TData = FindOperationChainItems["response"],
  TQueryData = FindOperationChainItems["response"],
  TQueryKey extends QueryKey = FindOperationChainItemsQueryKey,
>(
  operationChainId: FindOperationChainItemsPathParams["operationChainId"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindOperationChainItems["response"],
        FindOperationChainItems["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindOperationChainItems["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindOperationChainItems["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findOperationChainItemsQueryKey(operationChainId)
  const query = useQuery<
    FindOperationChainItems["data"],
    FindOperationChainItems["error"],
    TData,
    any
  >({
    ...findOperationChainItemsQueryOptions<TData, TQueryData>(
      operationChainId,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindOperationChainItems["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
